import { forwardRef, useContext } from 'react';

import { styled } from '@spazious/dollhouse-theme';
import { useForkRef } from '@spazious/react-hooks';

import { Flex, FlexElement, FlexProps } from '../Flex/index.js';

import { PanelContext } from './PanelContext.js';

export type PanelContentElement = FlexElement;

export interface PanelContentProps extends FlexProps {
  scroll?: 'vertical' | 'horizontal' | 'both' | 'none';
}

const scrollMapping = {
  vertical: 'overflow-y: auto;',
  horizontal: 'overflow-x: auto;',
  both: 'overflow: auto;',
  none: 'overflow: hidden;',
} as const;

export const PanelContent = styled(
  forwardRef<PanelContentElement, PanelContentProps>((props, ref) => {
    const ctx = useContext(PanelContext);

    if (!ctx) {
      throw new Error('PanelContent must be used within a Panel.');
    }

    const [, { ref: setScrollRef, ...restScrollProps }] = ctx;

    return (
      <Flex
        ref={useForkRef<PanelContentElement>(ref, setScrollRef)}
        column
        {...props}
        {...restScrollProps}
      />
    );
  })
).withConfig({
  shouldForwardProp: (prop) => prop !== 'scroll',
  displayName: 'PanelContent',
})<PanelContentProps>`
  padding: calc(2 * var(--spacing-padding));
  overflow-x: hidden;
  ${({ scroll = 'vertical' }) => scrollMapping[scroll]}
  height: 100%;
  padding-top: var(--spacing-padding);
  padding-bottom: var(--spacing-padding);
`;
